import { Card, Typography } from "antd";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/Common/PageHeader";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import ImageWithBasePath from "../../../components/Common/imageWithBasePath";
import ReactApexChart from "react-apexcharts";
import { formatMoney } from "../../../helpers/numberHelper";
import StatCard from "../../../components/Common/StatCard";
import useFinanceStats from "../hooks/useFinanceStats";
import Loader from "components/Common/loader";
import FinanceDashboardFilter from "../components/FinanceDashboardFilter";
import { useAbility } from "config/abilityContext";

const FinanceDashboard: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbItems = [
    { label: "dashboard", path: "/dashboard" },
    { label: "finance_dashboard", active: true },
  ];
  const routes = all_routes;
  const { dashboard, fetchFinanceDashboard, loading } = useFinanceStats();
  const [chartVersion, setChartVersion] = useState(0);
  const ability = useAbility();

  useEffect(() => {
    fetchFinanceDashboard({ period: "this_year" });
  }, []);

  // bar chart
  const [chartExpensesVsIncome, setChartExpensesVsIncome] = useState<any>({
    chart: {
      height: 350,
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "55%", endingShape: "rounded" },
    },
    colors: ["#7bc98c", "#f19898"],
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    series: [
      { name: t("fees"), data: [] },
      { name: t("expenses"), data: [] },
    ],
    xaxis: { categories: [] },
    yaxis: { title: { text: t("currency") + " (MAD)" } },
    fill: { opacity: 1 },
    tooltip: {
      y: { formatter: (val: any) => formatMoney(val) },
    },
  });

  // donut chart
  const [chartFeeByType, setChartFeeByType] = useState<any>({
    chart: { height: 350, type: "donut", toolbar: { show: false } },
    series: [],
    labels: [],
    colors: [],
    responsive: [{ breakpoint: 480, options: { chart: { width: 200 }, legend: { position: "bottom" } } }],
    tooltip: { y: { formatter: (value: number) => formatMoney(value) } },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: t("total_fees"),
              formatter: (w: any) => formatMoney(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)),
            },
          },
        },
      },
    },
  });

  const [chartExpensesByCategory, setChartExpensesByCategory] = useState<any>({
    chart: { height: 350, type: "donut", toolbar: { show: false } },
    series: [],
    labels: [],
    colors: [],
    responsive: [{ breakpoint: 480, options: { chart: { width: 200 }, legend: { position: "bottom" } } }],
    tooltip: { y: { formatter: (value: number) => formatMoney(value) } },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: t("total_expenses"),
              formatter: (w: any) => formatMoney(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)),
            },
          },
          value: {
            show: true,
            formatter: function (val: string, opts: any) {
              const seriesIndex = opts.seriesIndex;
              const label = opts.w.globals.labels[seriesIndex];
              const value = opts.w.globals.series[seriesIndex];
              return `${label}: ${formatMoney(value)}`;
            },
          },
          total: {
            show: true,
            label: t("total_expenses"),
            formatter: (w: any) => formatMoney(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)),
          },
        },
      },
    },
  });

  const [chartByComplitedPayment, setChartByComplitedPayment] = useState<any>({
    chart: { height: 350, type: "radialBar", toolbar: { show: false } },
    colors: ["#A7F3D0", "#FDE68A", "#FCA5A5", "#BFDBFE"],
    series: [],
    labels: ["fees_status.paid", "fees_status.partially_paid", "fees_status.overdue", "fees_status.coming"],
    stroke: { lineCap: "round" },
    plotOptions: {
      radialBar: {
        dataLabels: {
          total: {
            show: true,
            label: t("total_fees"),
            formatter: (w: any) => w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0) + "%",
          },
          value: { formatter: (val: number) => val + "%" },
        },
      },
    },
  });

  useEffect(() => {
    if (!dashboard) return;

    setChartExpensesVsIncome((prev: any) => {
      const labels =
        dashboard.incomeVsExpenses?.labels ||
        (dashboard.incomeVsExpenses?.months?.map((m: string) => t("months_short." + m))) ||
        [];

      return {
        ...prev,
        series: [
          { name: t("fees"), data: dashboard.incomeVsExpenses?.income || [] },
          { name: t("expenses"), data: dashboard.incomeVsExpenses?.expenses || [] },
        ],
        xaxis: { ...prev.xaxis, categories: labels },
      };
    });

    setChartFeeByType((prev: any) => ({
      ...prev,
      series: dashboard.feesByType?.series || [],
      labels: dashboard.feesByType?.labels || [],
      colors: dashboard.feesByType?.colors || [],
    }));

    setChartByComplitedPayment((prev: any) => ({
      ...prev,
      series: dashboard.feesByStatus?.series || [],
      labels: dashboard.feesByStatus?.labels?.map((label: string) => t("fees_status." + label)) || [],
      colors: dashboard.feesByStatus?.colors || [],
    }));

    setChartExpensesByCategory((prev: any) => ({
      ...prev,
      series: dashboard.expensesByCategory?.series || [],
      labels: dashboard.expensesByCategory?.labels || [],
      colors: dashboard.expensesByCategory?.colors || [],
    }));

    setChartVersion((v) => v + 1);
  }, [dashboard, t]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <PageHeader title={t("finance_dashboard")} breadcrumbItems={[]} />
          {/* Filters */}
          <div className="row">
            <div className="col-12 ">
              <FinanceDashboardFilter
                loading={loading}
                initialPeriod="this_year"
                onChange={(params) => {
                  // params: { period } OR { start, end }
                  fetchFinanceDashboard(params);
                }}
                onReset={() => {
                  fetchFinanceDashboard({ period: "this_year" });
                }}
              />

            </div>
          </div>
          {/* /Filters */}

          {loading && <Loader />}

          {!loading && (
            <div>

              <div className="row">
                {/* cards */}
                <div className="col-xxl-12 d-flex">
                  <div className="row flex-fill">
                    {ability.can("getAll", "Fee") && (
                      <div className="col-xl-4 col-md-4">
                        <StatCard
                          color="green"
                          icon="cash"
                          title={t("total_fees")}
                          value={formatMoney(dashboard?.statistics?.totalFeesPaid || 0)}
                        />
                      </div>
                    )}
                    {ability.can("getAll", "Expense") && (
                      <div className="col-xl-4 col-md-4">
                        <StatCard
                          color="blue"
                          icon="receipt"
                          title={t("total_expenses")}
                          value={formatMoney(dashboard?.statistics?.totalExpensesApproved || 0)}
                        />
                      </div>
                    )}
                    {ability.can("getAll", "Fee") && (
                      <div className="col-xl-4 col-md-4">
                        <StatCard
                          color="red"
                          icon="alert-circle"
                          title={t("unpaid_fee")}
                          value={dashboard?.statistics?.pendingFeesCount || 0}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* /cards */}
              </div>

              <div className="row">
                {/* bar chart */}
                {ability.can("getAll", "Fee") && ability.can("getAll", "Expense") && (
                  <div className="col-xxl-6 col-xl-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-header d-flex align-items-center justify-content-between">
                        <h4 className="card-title">{t("income_vs_expenses")}</h4>
                      </div>
                      <div className="card-body pb-0">
                        <div id="chartExpensesVsIncome" />
                        <ReactApexChart
                          key={`chartExpensesVsIncome-${chartVersion}`}
                          options={chartExpensesVsIncome}
                          series={chartExpensesVsIncome.series}
                          type="bar"
                          height={350}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {ability.can("getAll", "Fee") && (
                  <div className="col-xxl-6 col-xl-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-header d-flex align-items-center justify-content-between">
                        <h4 className="card-title">{t("fee_by_type")}</h4>
                      </div>
                      <div className="card-body pb-0">
                        <div id="chartFeeByType" />
                        <ReactApexChart
                          key={`chartFeeByType-${chartVersion}`}
                          options={chartFeeByType}
                          series={chartFeeByType.series}
                          type="donut"
                          height={350}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="row">
                {ability.can("getAll", "Fee") && (
                  <div className="col-xxl-6 col-xl-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                        <h4 className="card-title mb-3">{t("student_payment_status")}</h4>
                      </div>
                      <div className="card-body">
                        <div id="chartByComplitedPayment" />
                        <ReactApexChart
                          key={`chartByComplitedPayment-${chartVersion}`}
                          options={chartByComplitedPayment}
                          series={chartByComplitedPayment.series}
                          type="radialBar"
                          height={350}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {ability.can("getAll", "Expense") && (
                  <div className="col-xxl-6 col-xl-6 d-flex">
                    <div className="card flex-fill">
                      <div className="card-header d-flex align-items-center justify-content-between">
                        <h4 className="card-title">{t("expenses_by_category")}</h4>
                      </div>
                      <div className="card-body pb-0">
                        <div id="chartExpensesByCategory" />
                        <ReactApexChart
                          key={`chartExpensesByCategory-${chartVersion}`}
                          options={chartExpensesByCategory}
                          series={chartExpensesByCategory.series}
                          type="donut"
                          height={350}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FinanceDashboard;
